import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMentorApplicationsPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchApplications();
    }
  }, [user]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/mentor/applications`, {
        withCredentials: true
      });
      
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Error fetching mentor applications:', error);
      alert(language === 'tr' 
        ? '❌ Başvurular yüklenirken hata oluştu!' 
        : '❌ Error loading applications!');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      await axios.patch(
        `${API}/mentor/applications/${applicationId}?status=${newStatus}`,
        {},
        { withCredentials: true }
      );
      
      alert(language === 'tr' 
        ? `✅ Başvuru ${newStatus === 'approved' ? 'onaylandı' : 'reddedildi'}!` 
        : `✅ Application ${newStatus === 'approved' ? 'approved' : 'rejected'}!`);
      
      fetchApplications();
    } catch (error) {
      console.error('Error updating application:', error);
      alert(language === 'tr' 
        ? '❌ Güncelleme hatası!' 
        : '❌ Update failed!');
    }
  };

  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true;
    return app.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status) => {
    if (language === 'tr') {
      switch (status) {
        case 'approved': return 'Onaylandı';
        case 'rejected': return 'Reddedildi';
        case 'pending': return 'Beklemede';
        default: return status;
      }
    } else {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Navbar title={language === 'tr' ? 'Admin - Mentör Başvuruları' : 'Admin - Mentor Applications'} />
      
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {language === 'tr' ? '👨‍🏫 Mentör Başvuruları' : '👨‍🏫 Mentor Applications'}
          </h1>
          <p className="text-gray-600">
            {language === 'tr' 
              ? 'Mentör başvurularını inceleyin ve onaylayın' 
              : 'Review and approve mentor applications'}
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            {language === 'tr' ? 'Tümü' : 'All'} ({applications.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            {language === 'tr' ? 'Bekleyen' : 'Pending'} ({applications.filter(a => a.status === 'pending').length})
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'approved'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            {language === 'tr' ? 'Onaylanan' : 'Approved'} ({applications.filter(a => a.status === 'approved').length})
          </button>
          <button
            onClick={() => setFilter('rejected')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === 'rejected'
                ? 'bg-red-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            {language === 'tr' ? 'Reddedilen' : 'Rejected'} ({applications.filter(a => a.status === 'rejected').length})
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">
              {language === 'tr' ? 'Yükleniyor...' : 'Loading...'}
            </p>
          </div>
        )}

        {/* Applications List */}
        {!loading && filteredApplications.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <p className="text-gray-500">
              {language === 'tr' ? 'Başvuru bulunamadı' : 'No applications found'}
            </p>
          </div>
        )}

        {!loading && filteredApplications.length > 0 && (
          <div className="space-y-4">
            {filteredApplications.map((app) => (
              <div
                key={app.id}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{app.full_name}</h3>
                    <p className="text-gray-600">{app.email}</p>
                    <p className="text-gray-600">{app.phone}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(app.status)}`}>
                    {getStatusText(app.status)}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      {language === 'tr' ? 'Uzmanlık Alanları' : 'Expertise Areas'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {app.expertise_areas?.map((area, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      {language === 'tr' ? 'Deneyim' : 'Experience'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {app.experience_years} {language === 'tr' ? 'yıl' : 'years'}
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    {language === 'tr' ? 'Eğitim Geçmişi' : 'Education Background'}
                  </p>
                  <p className="text-sm text-gray-600">{app.education_background}</p>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-500">
                    {language === 'tr' ? 'Başvuru Tarihi:' : 'Applied on:'}{' '}
                    {new Date(app.created_at).toLocaleDateString()}
                  </p>
                </div>

                {app.notes && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-1">
                      {language === 'tr' ? 'Notlar' : 'Notes'}
                    </p>
                    <p className="text-sm text-gray-600">{app.notes}</p>
                  </div>
                )}

                {app.status === 'pending' && (
                  <div className="flex gap-3 mt-4 pt-4 border-t">
                    <button
                      onClick={() => handleStatusUpdate(app.id, 'approved')}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition"
                    >
                      ✅ {language === 'tr' ? 'Onayla' : 'Approve'}
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(app.id, 'rejected')}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition"
                    >
                      ❌ {language === 'tr' ? 'Reddet' : 'Reject'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMentorApplicationsPage;
